// Typed calls for the Gantt, milestones and people pages (plan §4, spec F9, F10), plus the pack's
// capacity for Leads roles. Query values are encoded here, so pages pass plain values.
import { createHttp, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  capacitySchema,
  ganttPageSchema,
  milestonePageSchema,
  peoplePageSchema,
  personLoadSchema,
  type CapacityView,
  type GanttPage,
  type MilestonePage,
  type PeoplePage,
  type PersonLoad,
} from './schemas/planning.ts';

export * from './schemas/planning.ts';

export type QueryValues = Readonly<Record<string, string | number | boolean | undefined>>;

/** "?a=1&b=x" from the defined values, or "" when there are none. */
export function queryString(values: QueryValues): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== '') params.set(k, String(v));
  });
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export interface PlanningApi {
  gantt(query: QueryValues, signal?: AbortSignal): Promise<GanttPage>;
  milestones(query: QueryValues, signal?: AbortSignal): Promise<MilestonePage>;
  people(query: QueryValues, signal?: AbortSignal): Promise<PeoplePage>;
  personLoad(id: number, query: QueryValues, signal?: AbortSignal): Promise<PersonLoad>;
  /** GET /leads/capacity: Leads roles only (leads.read). */
  capacity(signal?: AbortSignal): Promise<CapacityView>;
}

export function createPlanningApi(options: HttpOptions = {}): PlanningApi {
  const http = createHttp(options);
  const get = <T>(path: string, schema: Parameters<typeof http.json<T>>[1], signal?: AbortSignal) =>
    http.json({ method: 'GET', path, signal }, schema);
  return {
    gantt: (q, signal) => get(`/gantt${queryString(q)}`, ganttPageSchema, signal),
    milestones: (q, signal) => get(`/milestones${queryString(q)}`, milestonePageSchema, signal),
    people: (q, signal) => get(`/people${queryString(q)}`, peoplePageSchema, signal),
    personLoad: (id, q, signal) =>
      get(`/people/${pathSegment(id)}/load${queryString(q)}`, personLoadSchema, signal),
    capacity: (signal) => get('/leads/capacity', capacitySchema, signal),
  };
}
